import { useCallback, useMemo, useState } from 'react';
import { Upload, Scissors, Loader2, Info, Download } from 'lucide-react';

type NonSilentSegment = {
  startSample: number;
  endSample: number;
};

const DEFAULT_THRESHOLD = 0.02;
const DEFAULT_MIN_SILENCE_MS = 400;

type WindowWithWebkitAudioContext = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if ('AudioContext' in window) {
    return new AudioContext();
  }
  // 部分舊版 Safari 仍使用 webkit 前綴
  const windowWithWebkit = window as WindowWithWebkitAudioContext;
  if (windowWithWebkit.webkitAudioContext) {
    return new windowWithWebkit.webkitAudioContext();
  }
  return null;
}

/**
 * 計算音訊中「有聲音」的區段（排除長時間靜音）
 */
export function detectNonSilentSegments(
  buffer: AudioBuffer,
  {
    threshold = DEFAULT_THRESHOLD,
    minSilenceMs = DEFAULT_MIN_SILENCE_MS,
  }: { threshold?: number; minSilenceMs?: number } = {}
): NonSilentSegment[] {
  const { length, numberOfChannels, sampleRate } = buffer;
  if (length === 0) return [];

  const minSilenceSamples = Math.max(
    1,
    Math.floor((sampleRate * minSilenceMs) / 1000)
  );

  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < numberOfChannels; ch++) {
    channelData.push(buffer.getChannelData(ch));
  }

  const samplesMaxAbs = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    let maxAbs = 0;
    for (let ch = 0; ch < numberOfChannels; ch++) {
      const v = Math.abs(channelData[ch][i]);
      if (v > maxAbs) maxAbs = v;
    }
    samplesMaxAbs[i] = maxAbs;
  }

  const isSilent = (index: number) => samplesMaxAbs[index] < threshold;

  const segments: NonSilentSegment[] = [];
  let currentStart = 0;
  let inSilence = isSilent(0);
  let silenceStart = inSilence ? 0 : -1;

  for (let i = 1; i < length; i++) {
    const silent = isSilent(i);
    if (!inSilence && silent) {
      inSilence = true;
      silenceStart = i;
    } else if (inSilence && !silent) {
      const silenceLen = i - silenceStart;
      if (silenceLen >= minSilenceSamples) {
        if (silenceStart > currentStart) {
          segments.push({
            startSample: currentStart,
            endSample: silenceStart,
          });
        }
        currentStart = i;
      }
      inSilence = false;
      silenceStart = -1;
    }
  }

  if (inSilence) {
    const silenceLen = length - silenceStart;
    if (silenceLen < minSilenceSamples) {
      segments.push({ startSample: currentStart, endSample: length });
    } else if (silenceStart > currentStart) {
      segments.push({ startSample: currentStart, endSample: silenceStart });
    }
  } else if (currentStart < length) {
    segments.push({ startSample: currentStart, endSample: length });
  }

  return segments.filter(s => s.endSample > s.startSample);
}

/**
 * 將多段非靜音區段接在一起，輸出新的 AudioBuffer
 */
export function stitchNonSilentSegments(
  ctx: AudioContext,
  buffer: AudioBuffer,
  segments: NonSilentSegment[]
): AudioBuffer {
  if (segments.length === 0) {
    return ctx.createBuffer(buffer.numberOfChannels, 1, buffer.sampleRate);
  }

  const { numberOfChannels, sampleRate } = buffer;
  const totalSamples = segments.reduce(
    (sum, seg) => sum + (seg.endSample - seg.startSample),
    0
  );
  const out = ctx.createBuffer(numberOfChannels, totalSamples, sampleRate);

  for (let ch = 0; ch < numberOfChannels; ch++) {
    const inData = buffer.getChannelData(ch);
    const outData = out.getChannelData(ch);
    let offset = 0;
    for (const seg of segments) {
      const len = seg.endSample - seg.startSample;
      outData.set(inData.subarray(seg.startSample, seg.endSample), offset);
      offset += len;
    }
  }

  return out;
}

/**
 * 將 AudioBuffer 轉成 WAV Blob（16-bit PCM）
 */
export function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const { numberOfChannels, length, sampleRate } = buffer;
  const bytesPerSample = 2;
  const blockAlign = numberOfChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = length * blockAlign;
  const bufferSize = 44 + dataSize;
  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  let offset = 0;

  writeString(offset, 'RIFF');
  offset += 4;
  view.setUint32(offset, 36 + dataSize, true);
  offset += 4;
  writeString(offset, 'WAVE');
  offset += 4;
  writeString(offset, 'fmt ');
  offset += 4;
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numberOfChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, byteRate, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, bytesPerSample * 8, true);
  offset += 2;
  writeString(offset, 'data');
  offset += 4;
  view.setUint32(offset, dataSize, true);
  offset += 4;

  const channelData: Float32Array[] = [];
  for (let ch = 0; ch < numberOfChannels; ch++) {
    channelData.push(buffer.getChannelData(ch));
  }

  const maxInt16 = 0x7fff;
  for (let i = 0; i < length; i++) {
    for (let ch = 0; ch < numberOfChannels; ch++) {
      let sample = channelData[ch][i];
      sample = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, sample * maxInt16, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/**
 * 音檔去靜音：上傳音檔，偵測長時間無聲區段並刪除，只保留有說話的部分
 */
export function AudioTrimSilenceTool() {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalDuration, setOriginalDuration] = useState<number | null>(null);
  const [trimmedDuration, setTrimmedDuration] = useState<number | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [silenceThreshold, setSilenceThreshold] = useState(DEFAULT_THRESHOLD);
  const [minSilenceMs, setMinSilenceMs] = useState<number>(
    DEFAULT_MIN_SILENCE_MS
  );
  const [minSilenceMsInput, setMinSilenceMsInput] = useState<string>(
    String(DEFAULT_MIN_SILENCE_MS)
  );

  const thresholdLabel = useMemo(() => {
    if (silenceThreshold <= 0.01) return '非常敏感（容易誤判雜訊為說話）';
    if (silenceThreshold <= 0.03) return '建議值（一般人聲）';
    return '較嚴格（只保留較大聲片段）';
  }, [silenceThreshold]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      setFile(f);
      setError(null);
      setOriginalDuration(null);
      setTrimmedDuration(null);
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
      }
      setMinSilenceMs(DEFAULT_MIN_SILENCE_MS);
      setMinSilenceMsInput(String(DEFAULT_MIN_SILENCE_MS));
    },
    [downloadUrl]
  );

  const handleProcess = useCallback(async () => {
    if (!file) {
      setError('請先選擇音檔');
      return;
    }

    const ctx = getAudioContext();
    if (!ctx) {
      setError(
        '此瀏覽器不支援 Web Audio API，請改用最新版本 Chrome / Edge / Safari。'
      );
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));

      setOriginalDuration(audioBuffer.duration);

      const segments = detectNonSilentSegments(audioBuffer, {
        threshold: silenceThreshold,
        minSilenceMs,
      });

      if (segments.length === 0) {
        setError(
          '偵測不到明顯有聲音的區段，請嘗試調低靜音門檻或確認錄音內容。'
        );
        return;
      }

      const trimmedBuffer = stitchNonSilentSegments(ctx, audioBuffer, segments);
      setTrimmedDuration(trimmedBuffer.duration);

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
      }

      const wavBlob = audioBufferToWavBlob(trimmedBuffer);
      const url = URL.createObjectURL(wavBlob);
      setDownloadUrl(url);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : '處理音檔時發生錯誤，請稍後再試。';
      setError(msg);
    } finally {
      setProcessing(false);
      void ctx.close();
    }
  }, [file, silenceThreshold, minSilenceMs, downloadUrl]);

  const formatDuration = (seconds: number | null) => {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.max(0, seconds);
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 space-y-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            上傳錄音檔，工具會偵測長時間無人說話的區段並刪除，只保留有聲音的部分，適合剪掉會議空檔、直播開場等待時間等。
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              選擇音檔（建議 MP3 / WAV / M4A）
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
                <Upload className="h-4 w-4" />
                <span>選擇檔案</span>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {file
                  ? `${file.name}（${(file.size / 1024 / 1024).toFixed(2)} MB）`
                  : '尚未選擇檔案'}
              </span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                靜音門檻（振幅）
              </label>
              <input
                type="range"
                min={0.005}
                max={0.08}
                step={0.005}
                value={silenceThreshold}
                onChange={e => setSilenceThreshold(parseFloat(e.target.value))}
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                目前：{silenceThreshold.toFixed(3)}（{thresholdLabel}）
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                視為靜音的最短時間（毫秒）
              </label>
              <input
                type="number"
                min={100}
                max={2000}
                step={50}
                value={minSilenceMsInput}
                onChange={e => setMinSilenceMsInput(e.target.value)}
                onBlur={e => {
                  const raw = e.target.value.trim();
                  const parsed = Number(raw);
                  const next = Math.min(
                    2000,
                    Math.max(
                      100,
                      Number.isFinite(parsed) ? parsed : DEFAULT_MIN_SILENCE_MS
                    )
                  );
                  setMinSilenceMs(next);
                  setMinSilenceMsInput(String(next));
                }}
                className="w-24 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                例如 400 代表連續 0.4 秒幾乎沒聲音才會被視為靜音。
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <button
            type="button"
            onClick={handleProcess}
            disabled={!file || processing}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                處理中…
              </>
            ) : (
              <>
                <Scissors className="h-4 w-4" />
                去除靜音並產生新檔
              </>
            )}
          </button>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">
                長度比較
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                原始：{formatDuration(originalDuration)}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                去靜音後：{formatDuration(trimmedDuration)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">
                下載
              </p>
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  download={
                    file
                      ? file.name.replace(/\.[^.]+$/, '') + '-trimmed.wav'
                      : 'trimmed-audio.wav'
                  }
                  className="inline-flex items-center gap-2 rounded bg-gray-700 px-3 py-2 text-xs font-medium text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"
                >
                  <Download className="h-4 w-4" />
                  下載去靜音音檔（WAV）
                </a>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  產生完成後，會在這裡提供下載連結。
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 text-sm leading-relaxed text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            使用說明
          </h2>
        </div>
        <ul className="list-inside list-disc space-y-1">
          <li>本工具在瀏覽器內完成處理，不會上傳音檔到伺服器，隱私較安全。</li>
          <li>
            「靜音門檻」越小，越容易把微弱雜訊當成有聲音；若留下太多空白，可稍微調高。
          </li>
          <li>
            「最短靜音時間」可以避免每個字之間的小間隔被剪得太碎，建議介於
            300～600 毫秒。
          </li>
          <li>輸出為 WAV 16-bit PCM，方便再拿去剪輯或轉成其他格式。</li>
        </ul>
      </section>
    </>
  );
}
